import React, { useState } from "react";
import {
  X,
  FileBadge,
  CheckCircle,
  Loader,
  PauseCircle,
  UserCheck,
  RotateCcw,
  CircleSlash,
} from "lucide-react";
import frameBg from "../../../Assets/FrameBGDashboard.png";
import { ReassignTask } from "../../../Services/TaskService";
import { getProjectTeam } from "../../../Services/ProjectService";
import NotificationAlert from "../../Alerts/NotificationAlert";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { useNavigate } from "react-router-dom";

const ReassignTaskModal = ({ show, onClose, taskData, onReassign }) => {
  const navigate = useNavigate();
  const [remark, setRemark] = useState("");
  const [estimatedDate, setEstimatedDate] = useState("");
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assigneeUID, setAssigneeUID] = useState("");

  const isFutureDateTime = (date) => {
    if (!date) return false;
    return date.getTime() > new Date().getTime();
  };

  React.useEffect(() => {
    if (taskData?.projectUID && show) {
      fetchTeam();
    }
  }, [taskData, show]);

  const fetchTeam = async () => {
    try {
      const response = await getProjectTeam(taskData.projectUID);

      if (response.data?.status === "Success") {
        setTeamMembers(response.data.teamMemebers || []);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: error.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
    }
  };

  if (!show || !taskData) return null;

  const handleReassign = async () => {
    if (!estimatedDate) {
    setAlert({
      type: "error",
      message: "Please select estimated completion date.",
    });
    return;
  }

  if (!isFutureDateTime(new Date(estimatedDate))) {
    setAlert({
      type: "error",
      message: "Estimated completion date & time must be in the future.",
    });
    return;
  }

  if (!assigneeUID) {
    setAlert({
      type: "error",
      message: "Please select a team member for reassignment.",
    });
    return;
  }

    try {
      setLoading(true);

      const severityMapping = { High: 1, Moderate: 2, Low: 3 };

      const payload = {
        taskUID: taskData.taskUID,
        taskNo: taskData.taskNo,
        reAssignedFromName: taskData.assignedTo,
        severity: severityMapping[taskData.severity] || 1,
        reAssigneToUID: assigneeUID,
        estimatedComplete: estimatedDate,
        remark: remark || null,
      };

      const response = await ReassignTask(payload);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({ type: "success", message: data.message });
        onReassign();
        setRemark("");

        setTimeout(() => {
          onClose();
          setAlert(null);
        }, 4000);
      } else {
        setAlert({
          type: "error",
          message: data.message || "Failed to update task",
        });
      }
    } catch (error) {
      if (error.response?.status === 400) {
        navigate("/bad-request", {
          state: {
            error: error.response?.data?.message || "Bad request (400)",
          },
        });
        return;
      }
      const msg =
        error.response?.data?.message ||
        error.response?.data?.Message ||
        "Server error";
      setAlert({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "Assigned":
        return {
          icon: <UserCheck size={14} className="text-indigo-700" />,
          text: "Assigned",
          style: "bg-indigo-100 text-indigo-700",
        };

      case "In Progress":
        return {
          icon: <Loader size={14} className="text-blue-700 animate-spin" />,
          text: "In Progress",
          style: "bg-blue-100 text-blue-700",
        };

      case "Completed":
        return {
          icon: <CheckCircle size={14} className="text-green-700" />,
          text: "Completed",
          style: "bg-green-100 text-green-700",
        };

      case "Re-open":
        return {
          icon: <RotateCcw size={14} className="text-purple-700" />,
          text: "Re-open",
          style: "bg-purple-100 text-purple-700",
        };

      case "Closed":
        return {
          icon: <CircleSlash size={14} className="text-gray-700" />,
          text: "Closed",
          style: "bg-gray-200 text-gray-700",
        };

      case "Hold":
        return {
          icon: <PauseCircle size={14} className="text-yellow-700" />,
          text: "On Hold",
          style: "bg-yellow-100 text-yellow-700",
        };

      default:
        return {
          icon: <FileBadge size={14} className="text-gray-600" />,
          text: status,
          style: "bg-gray-100 text-gray-600",
        };
    }
  };
  const statusInfo = getStatusInfo(taskData.status);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
        onClick={onClose}
      >
        <div
          className="bg-white  w-full max-w-xl h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="px-5 py-4 border-b flex items-center justify-between text-white shadow-md bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${frameBg})` }}
          >
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold tracking-wide">
                Re-Assign Task
              </h2>

              <span className="mt-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium w-fit">
                #{taskData.taskNo}
              </span>
            </div>

            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition"
            >
              <X size={26} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Task Details */}
            <div className="bg-white border shadow-sm rounded-xl p-5 space-y-4 hover:shadow-md transition">
              <div className="flex items-center justify-between w-full">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FileBadge size={16} className="text-blue-600" />
                  Task Details
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusInfo.style}`}
                >
                  {statusInfo.icon}
                  {statusInfo.text}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 text-[0.93rem] text-gray-700">
                <p>
                  <span className="font-semibold">Description:</span>{" "}
                  {taskData.description}
                </p>

                <p>
                  <span className="font-semibold">Assigned By:</span>{" "}
                  {taskData.assignedByEmail}
                </p>

                <p>
                  <span className="font-semibold">Project:</span>{" "}
                  {taskData.projectName}
                </p>

                <p>
                  <span className="font-semibold">Expected Complete Date:</span>{" "}
                  {formatDateTime(taskData.estimatedComplete)}
                </p>

                <p>
                  <span className="font-semibold">Created At:</span>{" "}
                  {formatDateTime(taskData.createdAt)}
                </p>

                <p>
                  <span className="font-semibold">Severity:</span>{" "}
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold w-fit
                    ${
                      taskData.severity === "High"
                        ? "bg-red-100 text-red-700"
                        : taskData.severity === "Moderate"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                    }`}
                  >
                    {taskData.severity}
                  </span>
                </p>
              </div>
            </div>

            {/* Re-assign To */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Re-Assign To <span className="text-red-500">*</span>
              </label>

              <select
                value={assigneeUID}
                onChange={(e) => setAssigneeUID(e.target.value)}
                className="w-full p-3 border rounded-lg text-sm bg-white focus:outline-none 
            focus:ring-2 focus:ring-blue-400 shadow-sm"
              >
                <option value="">Select team member</option>

                {teamMembers.map((member) => (
                  <option key={member.userUID} value={member.userUID}>
                    {member.name} ({member.role})
                  </option>
                ))}
              </select>
            </div>

            {/* Estimated Date */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Estimated Completion <span className="text-red-500">*</span>
              </label>
              <Flatpickr
                value={estimatedDate ? new Date(estimatedDate) : null}
                onReady={(selectedDates, dateStr, instance) => {
                  // force initial valid range
                  const dt = instance.selectedDates[0] || new Date();

                  const min = new Date(dt);
                  min.setHours(9, 30, 0, 0);

                  const max = new Date(dt);
                  max.setHours(18, 0, 0, 0);

                  instance.config.minTime = "09:30";
                  instance.config.maxTime = "18:00";
                }}
                onValueUpdate={(selectedDates, dateStr, instance) => {
                  if (!selectedDates.length) return;

                  const dt = selectedDates[0];

                  const min = new Date(dt);
                  min.setHours(9, 30, 0, 0);

                  const max = new Date(dt);
                  max.setHours(18, 0, 0, 0);

                  // if time goes below 9:30 AM → pull it back
                  if (dt < min) {
                    instance.setDate(min, false);
                  }

                  // if above 6 PM → push to next-day 9:30 AM
                  if (dt > max) {
                    const nextDay = new Date(dt);
                    nextDay.setDate(dt.getDate() + 1);
                    nextDay.setHours(9, 30, 0, 0);
                    instance.setDate(nextDay, false);
                  }
                }}
                onClose={(selectedDates, dateStr, instance) => {
                  if (!selectedDates?.length) return;

                  const final = instance.selectedDates[0];

                  if (!isFutureDateTime(final)) {
                    setAlert({
                      type: "error",
                      message:
                        "Estimated completion date & time must be in the future.",
                    });

                    setEstimatedDate("");
                    instance.clear();
                    return;
                  }

                  const localISO = new Date(
                    final.getTime() - final.getTimezoneOffset() * 60000,
                  )
                    .toISOString()
                    .slice(0, 19);

                  setEstimatedDate(localISO);
                }}
                options={{
                  enableTime: true,
                  dateFormat: "d-m-Y h:i K", // show AM/PM
                  time_24hr: false,
                  minuteIncrement: 15,
                  allowInput: true,

                  minTime: "09:30",
                  maxTime: "18:00",
                }}
                className="w-full p-3 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm"
              />
            </div>

            {/* Remarks */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Remarks
              </label>

              <textarea
                rows={4}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add your remark..."
                className="w-full p-3 border rounded-lg text-sm bg-white focus:outline-none 
                       focus:ring-2 focus:ring-blue-400 shadow-sm"
              ></textarea>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 items-center shadow-inner">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm border rounded-md bg-white text-gray-700 hover:bg-gray-100 shadow-sm transition"
            >
              Cancel
            </button>

            <button
              onClick={handleReassign}
              disabled={loading}
              className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow-md hover:opacity-90 transition"
            >
              {loading ? "Updating..." : "Re-Assign Task"}
            </button>
          </div>
        </div>

        {alert && (
          <NotificationAlert
            type={alert.type}
            message={alert.message}
            show={true}
            duration={4000}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
    </>
  );
};

export default ReassignTaskModal;
